import type {SourceLogoKind} from "~/components/source-logo/sourceLogos";
import {useInfoSourcesQuery} from "~/pages/supplier-360/hooks/useInfoSourcesQuery";

/**
 * The `InfoSource.name` that gates each provider's logo (api doc §2.6b) via its own `show_logo`
 * field. Mithra is our own source, so it has no row to check and always shows.
 *
 * Moved off per-customer config 2026-10-22, per the user — `SUPPLIER_360_SHOW_SV_LOGO`/
 * `SUPPLIER_360_SHOW_DNB_LOGO` were the wrong place for this: one config-key enum value per
 * provider, when `InfoSource` already has exactly one canonical row per provider to hold it on
 * directly. See that model's own docstring (cat-material).
 */
const LOGO_SOURCE_NAME: Record<SourceLogoKind, string | null> = {
    mithra: null,
    semanticVision: 'SV',
    dnb: 'Dun & Bradstreet',
};

/**
 * Whether a provider's logo may be shown. A missing or not-yet-loaded row counts as off, the
 * backend's own rule (§2.6b) — unchanged by the move off customer config.
 */
export function useSourceLogoAllowed(): (source: SourceLogoKind) => boolean {
    const {data: infoSources} = useInfoSourcesQuery();
    return source => {
        const name = LOGO_SOURCE_NAME[source];
        if (name === null) return true;
        return infoSources?.find(s => s.name === name)?.show_logo ?? false;
    };
}
